/**
 * @module queue
 * @description Public queue and scheduled-work adapter contract surface.
 * Runtime queue implementations live in provider packages; this subpath exports
 * only the interface, Zod schemas, and thin error class.
 */

import type {
  AdapterHealth as SharedAdapterHealth,
  AdapterListOptions as SharedAdapterListOptions,
  QueueAdapter as SharedQueueAdapter,
  QueueJob as SharedQueueJob,
  QueueJobInput as SharedQueueJobInput,
  QueueJobStatus as SharedQueueJobStatus,
} from "@geenius/adapters";
import {
  AdapterHealthSchema as SharedAdapterHealthSchema,
  AdapterListOptionsSchema as SharedAdapterListOptionsSchema,
  QueueContractError as SharedQueueContractError,
  QueueJobInputSchema as SharedQueueJobInputSchema,
  QueueJobSchema as SharedQueueJobSchema,
  QueueJobStatusSchema as SharedQueueJobStatusSchema,
} from "@geenius/adapters";

/**
 * Health result returned by queue providers after checking enqueue and worker
 * connectivity.
 *
 * @example
 * ```ts
 * const health: AdapterHealth = await queue.getHealth();
 * ```
 */
export type AdapterHealth = SharedAdapterHealth;

/**
 * Pagination options accepted by `QueueAdapter.listJobs()`.
 *
 * @example
 * ```ts
 * const options: AdapterListOptions = { limit: 25, prefix: "email" };
 * ```
 */
export type AdapterListOptions = SharedAdapterListOptions;

/**
 * SDK-free queue and scheduled-work adapter contract implemented by downstream
 * provider packages and app-owned queue modules.
 *
 * @example
 * ```ts
 * const job = await queue.enqueue({ name: "send-email", payload: { userId: "u1" } });
 * ```
 */
export type QueueAdapter = SharedQueueAdapter;

/**
 * Serializable queue job returned by queue providers.
 *
 * @example
 * ```ts
 * const job: QueueJob = { id: "job_1", name: "send-email", payload: {}, status: "queued", attempts: 0, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
 * ```
 */
export type QueueJob = SharedQueueJob;

/**
 * Provider-neutral enqueue payload accepted by queue adapters.
 *
 * @example
 * ```ts
 * const input: QueueJobInput = { name: "send-email", payload: { userId: "u1" } };
 * ```
 */
export type QueueJobInput = SharedQueueJobInput;

/**
 * Provider-neutral scheduled-job payload accepted by `QueueAdapter.schedule()`.
 *
 * @example
 * ```ts
 * const input: QueueScheduledJobInput = { name: "digest", runAt: new Date().toISOString() };
 * ```
 */
export type QueueScheduledJobInput = SharedQueueJobInput & { runAt: string };

/**
 * Queue job lifecycle status.
 *
 * @example
 * ```ts
 * const status: QueueJobStatus = "scheduled";
 * ```
 */
export type QueueJobStatus = SharedQueueJobStatus;

/**
 * Runtime validator for the common queue adapter health response.
 *
 * @example
 * ```ts
 * const health = AdapterHealthSchema.parse({ ok: true, checkedAt: new Date().toISOString() });
 * ```
 */
export const AdapterHealthSchema = SharedAdapterHealthSchema;

/**
 * Runtime validator for queue list pagination options.
 *
 * @example
 * ```ts
 * const options = AdapterListOptionsSchema.parse({ limit: 10 });
 * ```
 */
export const AdapterListOptionsSchema = SharedAdapterListOptionsSchema;

/**
 * Contract-layer error for invalid queue payloads or provider-boundary
 * violations before a concrete queue SDK receives the request.
 *
 * @example
 * ```ts
 * throw new QueueContractError("Scheduled queue jobs require runAt");
 * ```
 */
export const QueueContractError = SharedQueueContractError;

/**
 * Runtime validator for provider-neutral queue enqueue payloads.
 *
 * @example
 * ```ts
 * const input = QueueJobInputSchema.parse({ name: "send-email", payload: { userId: "u1" } });
 * ```
 */
export const QueueJobInputSchema = SharedQueueJobInputSchema;

/**
 * Runtime validator for scheduled queue payloads that require a run time.
 *
 * @example
 * ```ts
 * const input = QueueScheduledJobInputSchema.parse({ name: "digest", runAt: new Date().toISOString() });
 * ```
 */
export const QueueScheduledJobInputSchema = SharedQueueJobInputSchema.required({
  runAt: true,
}).refine((input) => input.runAt.trim().length > 0, {
  message: "Scheduled queue jobs require a non-empty runAt timestamp",
  path: ["runAt"],
});

/**
 * Runtime validator for serialized queue jobs returned by providers.
 *
 * @example
 * ```ts
 * const job = QueueJobSchema.parse({ id: "job_1", name: "send-email", payload: {}, status: "queued", attempts: 0, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
 * ```
 */
export const QueueJobSchema = SharedQueueJobSchema;

/**
 * Runtime validator for queue job lifecycle statuses.
 *
 * @example
 * ```ts
 * const status = QueueJobStatusSchema.parse("queued");
 * ```
 */
export const QueueJobStatusSchema = SharedQueueJobStatusSchema;
